// =============================================================================
// client/src/lib/can.js | v2.0 | S83 | client capability gating
// =============================================================================
// MOVED from client/src/lib/flux/can.js. It is no longer a FluxEngine file:
// the capability layer is Front Office and is CONSUMED BY flux surfaces, which
// is a different thing from living in them (spec §0, Jake's constraint).
//
// v2.0 reads the SERVER'S RESOLVED ANSWER — `me.permissions` from
// GET /api/auth/me — instead of re-deriving one from a role string against a
// hard-coded table. One question, one answer, computed once, server-side.
//
// 🔴 IT NEVER GRANTS. The server decides; this only hides what the server would
// refuse, so an operator is not offered a button that returns 403. When a 403
// does arrive it is rendered with the server's own message, verbatim. If this
// file and the server ever disagree, THE SERVER IS RIGHT BY CONSTRUCTION
// (spec §9 invariant 2).
//
// ── THE DEPLOY WINDOW, AND WHY THE OLD TABLE IS STILL HERE ──────────────────
// Vercel and Railway deploy independently, so this client can load against a
// server that has not shipped `permissions` yet — and against one that has,
// but whose `team` table has not been ALTERed, in which case every override is
// absent and only role defaults resolve. Both are real states, not edge cases.
//
// So: when a resolved map is present, it is the ONLY input. When it is absent,
// this falls back to LEGACY_RULES — the exact table this file shipped with at
// S80. That is honest degradation: the operator sees what they saw yesterday,
// and the server refuses anything the fallback gets wrong. Falling back to
// "hide everything" would make the app look broken for an admin; falling back
// to "show everything" would offer buttons that 403. Neither is better than
// showing the previous behaviour.
//
// 🔴 LEGACY_RULES IS A FALLBACK, NOT A SECOND SOURCE OF TRUTH. When the server
// answers, this table is not consulted. Do not add a key here — add it to
// server/src/lib/permissions.js, which is where a key gets a consumer.
// =============================================================================

const ADMIN_MANAGER = ['admin', 'manager'];
const ADMIN_ONLY = ['admin'];

// Everyone with a team login, EXCEPT system.
const ANY_HUMAN = ['admin', 'manager', 'recruiting_coordinator', 'scout', 'lead_specialist'];

// The S80 table, verbatim, kept ONLY for the deploy window described above.
const LEGACY_RULES = {
  'contacts.edit_identity': ADMIN_ONLY,
  'contacts.remove': ADMIN_ONLY,
  'contacts.import': ADMIN_ONLY,
  'oauth.invite': ANY_HUMAN,
  'oauth.disconnect': ADMIN_MANAGER,
  'viewers.manage': ADMIN_MANAGER,
  'recruit.assign_rc': ADMIN_MANAGER,
  // The pre-capability invoices route was requireRole(admin, manager). Keep
  // that exact deploy-window fallback; the resolved server map widens RC.
  'invoice.ask': ADMIN_MANAGER,
  'campaign.launch': ADMIN_ONLY,
  'campaign.pause': ADMIN_MANAGER,
  'campaign.resume': ADMIN_ONLY,
  'campaign.archive': ADMIN_MANAGER,
  // W-12 shipped: RC generates variants. Present so the fallback matches HEAD.
  'campaign.variants': ['admin', 'manager', 'recruiting_coordinator'],
};

function legacyCan(role, action) {
  if (!role || role === 'system') return false;
  const allowed = LEGACY_RULES[action];
  // An unknown action is REFUSED, never allowed. A typo in a call site should
  // hide a button, never reveal one.
  if (!allowed) return false;
  return allowed.includes(role);
}

/**
 * can(subject, action) -> boolean   "should this control render?"
 *
 * `subject` accepts, in order of preference:
 *   · the `me` object from useMe()   — { permissions, team: { role } }
 *   · a resolved permissions map     — { 'campaign.launch': true, ... }
 *   · a bare role string             — legacy, the S80 call shape
 *
 * Three shapes rather than one because the three existing call sites pass a
 * role today and the deploy window above means both inputs are live at once.
 * Prefer passing `me`.
 */
export function can(subject, action) {
  if (!subject) return false;

  if (action === 'campaign.launch' || action === 'campaign.resume' || action === 'campaign.transport_gate') {
    const immutableRole = typeof subject === 'string' ? subject : (subject.team?.role ?? subject.role);
    return immutableRole === 'admin';
  }

  // Legacy: a bare role string.
  if (typeof subject === 'string') return legacyCan(subject, action);

  // The me object: prefer its resolved map, fall back to its role.
  if (subject.permissions && typeof subject.permissions === 'object') {
    // `system` never renders a control regardless of what resolved, because it
    // is not a person (spec §9 invariant 5). Belt and braces: permissions.js
    // resolves system to nothing anyway.
    if (subject.team?.role === 'system') return false;
    return subject.permissions[action] === true;
  }

  const role = subject.team?.role ?? subject.role ?? null;
  if (role) return legacyCan(role, action);

  // A resolved map passed directly.
  if (typeof subject === 'object' && action in subject) return subject[action] === true;

  return false;
}

export const KNOWN_ACTIONS = Object.keys(LEGACY_RULES);

export default can;
